import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { InputComponent } from '../../../shared/components/input/input.component';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Pagination } from '../../../shared/services/pagination/pagination';
import { User } from '../../../shared/models/interfaces/user.interface';
import { UserService } from '../../../shared/services/user2/user.service';
import { debounceTime, take, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { StatusPillEnum } from '../../../shared/components/status-pill/status-pill.component';
import { Sorts } from '../../../shared/services/pagination/models/typies/sorts.type';
import { ButtonTypeEnum } from '../../../shared/components/button/types/button-type.enum';
import { ButtonSizeEnum } from '../../../shared/components/button/types/button-size.enum';
import { ButtonRadiusEnum } from '../../../shared/components/button/types/button-radius.enum';
import { ModalAction } from '../../../shared/modals/modal-actions/modal-action.interface';
import { UsersTableComponent } from '../components/users-table/users-table.component';
import { ModalService } from '../../../shared/services/modal/modal.service';
import { createCancelConfirmButtons, openConfirmModal } from '../../../helpers/modal.helper';
import { CreateUserModalComponent } from '../modals/create-user-modal/create-user-modal.component';
import { ImportUsersModalComponent } from '../modals/import-users-modal/import-users-modal.component';
import { InviteUserModalComponent } from '../modals/invite-user-modal/invite-user-modal.component';
import { ToastService } from '../../../shared/components/toast/service/toast.service';
import { getCrudErrorMessage } from '../../../shared/utils/functions';

@Component({
  imports: [
    IconComponent,
    ButtonComponent,
    InputComponent,
    ReactiveFormsModule,
    UsersTableComponent,
  ],
  templateUrl: './list.component.html',
})
export class ListComponent extends Pagination<User> implements OnInit, OnDestroy {

  constructor(
    private readonly modalService: ModalService,
    private readonly userService: UserService,
    private readonly toastService: ToastService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    super();
  }

  public readonly searchControl = new FormControl('');

  public readonly buttonTypeEnum = ButtonTypeEnum;

  public readonly buttonSizeEnum = ButtonSizeEnum;

  public readonly buttonRadiusEnum = ButtonRadiusEnum;

  public hasSSO = false;

  public page = 1;

  private isSearchListenerInitialized = false;

  private readonly destroy$$ = new Subject<void>();

  public async ngOnInit(): Promise<void> {
    this.size = 10;
    this.sort = { field: 'createdAt', order: 'DESC' };

    this.setupSearchListener();
    await this.loadUsers(1);
  }

  public override ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.complete();
    super.ngOnDestroy();
  }

  public openCreateUserModal(): void {
    const modalConfig = { width: '520px', modal: true, closable: false, showHeader: false };
    const ref = this.hasSSO
      ? this.modalService.open(InviteUserModalComponent, modalConfig)
      : this.modalService.open(CreateUserModalComponent, modalConfig);

    ref.onClose.pipe(take(1)).subscribe((result?: { reload: boolean }) => {
      if (result?.reload) {
        void this.reloadUsersList();
      }
    });
  }

  public openImportUsersModal(): void {
    const ref = this.modalService.open(ImportUsersModalComponent, {
      width: '580px',
      modal: true,
      closable: false,
      showHeader: false,
    });

    ref.onClose.pipe(take(1)).subscribe((result?: { reload: boolean }) => {
      if (result?.reload) {
        void this.reloadUsersList();
      }
    });
  }

  public getRowActions(user: User): ModalAction[] {
    const actions: ModalAction[] = [
      {
        label: 'Editar',
        icon: 'edit',
        callback: () => this.openEditUserModal(user),
      },
    ];

    if (this.isUserActive(user)) {
      actions.push({
        label: 'Desativar',
        icon: 'power_settings_new',
        callback: () => this.openUserStatusModal(user, false),
      });
    } else {
      actions.push({
        label: 'Ativar',
        icon: 'check_circle',
        callback: () => this.openUserStatusModal(user, true),
      });
    }

    actions.push({
      label: 'Excluir',
      icon: 'delete',
      colorClass: 'text-lDanger',
      callback: () => this.openUserDeleteModal(user),
    });

    return actions;
  }

  public isUserActive(user: User): boolean {
    return user.status === StatusPillEnum.ACTIVE && user.isActive !== false;
  }

  public getUserStatus(user: User): StatusPillEnum {
    return user.status || StatusPillEnum.INACTIVE;
  }

  public async reloadUsersList(): Promise<void> {
    await this.loadUsers(1);
  }

  public override async onChangeSort(event?: Sorts): Promise<void> {
    this.sort = event || { field: 'createdAt', order: 'DESC' };
    await this.loadUsers(1);
  }

  public override async onChangePage(page: number): Promise<void> {
    await this.loadUsers(page);
  }

  private setupSearchListener(): void {
    if (this.isSearchListenerInitialized) {
      return;
    }

    this.searchControl.valueChanges.pipe(
      debounceTime(400),
      takeUntil(this.destroy$$),
    ).subscribe(() => {
      void this.loadUsers(1);
    });

    this.isSearchListenerInitialized = true;
  }

  private async loadUsers(page: number): Promise<void> {
    try {
      this.isLoading = true;

      const safePage = Math.max(1, page || this.page || 1);

      this.source = await this.userService.getMany({
        page: safePage,
        limit: this.size,
        search: this.searchControl.value || '',
        sort: this.sort,
      });

      if (this.source.data.length === 0 && this.source.page > 1) {
        this.source = await this.userService.getMany({
          page: this.source.page - 1,
          limit: this.size,
          search: this.searchControl.value || '',
          sort: this.sort,
        });
      }

      this.page = this.source.page;
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  private openUserStatusModal(user: User, activate: boolean): void {
    const actionLabel = activate ? 'Ativar' : 'Desativar';

    openConfirmModal(
      this.modalService,
      'fit-content',
      {
        title: `${actionLabel} usuário`,
        description: `Tem certeza que deseja ${actionLabel.toLowerCase()} o usuário "${user.name}"?`,
        buttons: createCancelConfirmButtons(actionLabel, activate ? ButtonTypeEnum.PRIMARY : ButtonTypeEnum.DANGER),
      },
      async () => {
        await this.updateUserStatus(user, activate);
      },
      this.destroy$$,
    );
  }

  private async updateUserStatus(user: User, activate: boolean): Promise<void> {
    try {
      if (activate) {
        await this.userService.activate(user.id);
      } else {
        await this.userService.deactivate(user.id);
      }

      await this.loadUsers(this.page);
      this.toastService.success(activate ? 'Usuário ativado com sucesso.' : 'Usuário desativado com sucesso.');
    } catch (error) {
      this.toastService.error(getCrudErrorMessage(error), 'Oops...');
    }
  }

  private openUserDeleteModal(user: User): void {
    openConfirmModal(
      this.modalService,
      'fit-content',
      {
        title: 'Excluir usuário',
        description: `Tem certeza que deseja excluir o usuário "${user.name}"?`,
        buttons: createCancelConfirmButtons('Excluir', ButtonTypeEnum.DANGER),
      },
      async () => {
        await this.deleteUser(user);
      },
      this.destroy$$,
    );
  }

  private async deleteUser(user: User): Promise<void> {
    try {
      await this.userService.delete(user.id);
      await this.loadUsers(this.page);
      this.toastService.success('Usuário excluído com sucesso.');
    } catch (error) {
      this.toastService.error(getCrudErrorMessage(error), 'Oops...');
    }
  }

  private openEditUserModal(user: User): void {
    const ref = this.modalService.open(CreateUserModalComponent, {
      width: '520px',
      modal: true,
      closable: false,
      showHeader: false,
      data: {
        mode: 'edit',
        user,
      },
    });

    ref.onClose.pipe(take(1)).subscribe((result?: { reload: boolean }) => {
      if (result?.reload) {
        void this.reloadUsersList();
      }
    });
  }

}
